import React, { useState, useEffect } from 'react';
import CompanyProvidersContainer from './CompanyProvidersContainer';
import CompanyPatnerFormModal from './CompanyPatnerFormModal';
import ResidentialUnitsContainer from './ResidentialUnitsContainer';
import ResidentialUnitFormModal from './ResidentialUnitFormModal';
import ResidentialUnitsModal from './ResidentialUnitsModal';
import { toast } from 'react-toastify';
import companyPatnersService from '../services/companyPatnersService';
import unidadesResidencialesService from '../services/unidadesResidencialesService';

const CompanyProvidersTab = () => {
  // Estados para company patners y unidades residenciales
  const [companyPatners, setCompanyPatners] = useState([]);
  const [unidadesResidenciales, setUnidadesResidenciales] = useState([]);
  const [selectedCompanyPatnerId, setSelectedCompanyPatnerId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isUnitFormModalOpen, setIsUnitFormModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingUnidades, setLoadingUnidades] = useState(false);

  // Cargar datos al montar el componente
  useEffect(() => {
    loadCompanyPatners();
  }, []);

  // Cargar todos los company patners
  const loadCompanyPatners = async () => {
    try {
      setLoading(true);
      const data = await companyPatnersService.getCompanyPatners();
      setCompanyPatners(data);
    } catch (error) {
      toast.error('Error al cargar company patners: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  // Cargar unidades residenciales de un company patner específico
  const loadUnidadesByCompanyPatner = async (companyPatnerId) => {
    try {
      setLoadingUnidades(true);
      const data = await unidadesResidencialesService.getUnidadesByCompanyPatner(companyPatnerId);
      setUnidadesResidenciales(data);
    } catch (error) {
      toast.error('Error al cargar unidades residenciales: ' + (error.message || 'Error desconocido'));
      setUnidadesResidenciales([]);
    } finally {
      setLoadingUnidades(false);
    }
  };

  // Obtener el nombre del company patner seleccionado
  const selectedCompanyPatner = companyPatners.find(c => c.id === selectedCompanyPatnerId);
  const selectedCompanyPatnerName = selectedCompanyPatner ? selectedCompanyPatner.name : '';

  // Filtrar unidades por company patner seleccionado
  const filteredUnits = selectedCompanyPatnerId
    ? unidadesResidenciales.filter(unit => unit.company_patner_id === selectedCompanyPatnerId)
    : [];

  // Handlers para company patners
  const handleSelectCompanyPatner = async (companyPatnerId) => {
    setSelectedCompanyPatnerId(companyPatnerId);
    setIsModalOpen(true);
    // Cargar las unidades del company patner seleccionado
    await loadUnidadesByCompanyPatner(companyPatnerId);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenFormModal = () => {
    setIsFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
  };

  const handleOpenUnitFormModal = () => {
    setIsUnitFormModalOpen(true);
  };

  const handleCloseUnitFormModal = () => {
    setIsUnitFormModalOpen(false);
  };

  const handleAddCompanyPatner = async (newCompanyPatner) => {
    try {
      setLoading(true);

      // Validaciones adicionales
      if (!newCompanyPatner.name || newCompanyPatner.name.trim() === '') {
        toast.error('El nombre es requerido');
        return;
      }

      if (!newCompanyPatner.email_patner_company || newCompanyPatner.email_patner_company.trim() === '') {
        toast.error('El email es requerido');
        return;
      }

      const companyPatnerData = {
        name: newCompanyPatner.name,
        email_patner_company: newCompanyPatner.email_patner_company,
        telefono: newCompanyPatner.telefono || '',
        number_unidades: newCompanyPatner.number_unidades || 0,
        fecha: new Date().toISOString(),
        businness_patners: newCompanyPatner.businness_patners || 0,
        // Datos bancarios
        banco: newCompanyPatner.banco || '',
        numero_Cuenta: newCompanyPatner.numero_Cuenta || '',
        tipo_Cuenta: newCompanyPatner.tipo_Cuenta || '',
        nombre_Titular: newCompanyPatner.nombre_Titular || '',
        // Datos breve
        type_LLave: newCompanyPatner.type_LLave || '',
        number_LLave: newCompanyPatner.number_LLave || '',
        titular_Breve: newCompanyPatner.titular_Breve || ''
      };

      await companyPatnersService.createCompanyPatner(companyPatnerData);
      await loadCompanyPatners();
      toast.success('Company patner agregado exitosamente');
      handleCloseFormModal();
    } catch (error) {
      toast.error('Error al agregar company patner: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCompanyPatner = async (companyPatnerId) => {
    const companyPatner = companyPatners.find(c => c.id === companyPatnerId);
    if (companyPatner) {
      const newName = prompt('Nuevo nombre del company patner:', companyPatner.name);
      if (newName && newName.trim() !== '') {
        try {
          setLoading(true);
          await companyPatnersService.updateCompanyPatner(companyPatnerId, {
            ...companyPatner,
            name: newName.trim()
          });
          await loadCompanyPatners();
          toast.success('Company patner actualizado exitosamente');
        } catch (error) {
          toast.error('Error al actualizar company patner: ' + (error.message || 'Error desconocido'));
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleDeleteCompanyPatner = async (companyPatnerId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este company patner? Se eliminarán también todas sus unidades residenciales.')) {
      try {
        setLoading(true);
        await companyPatnersService.deleteCompanyPatner(companyPatnerId);
        await loadCompanyPatners();

        // Si era el company patner seleccionado, deseleccionar y limpiar unidades
        if (selectedCompanyPatnerId === companyPatnerId) {
          setSelectedCompanyPatnerId(null);
          setUnidadesResidenciales([]);
        }

        toast.success('Company patner eliminado exitosamente');
      } catch (error) {
        toast.error('Error al eliminar company patner: ' + (error.message || 'Error desconocido'));
      } finally {
        setLoading(false);
      }
    }
  };

  // Handlers para unidades residenciales
  const handleAddUnidad = async (newUnidad) => {
    try {
      setLoading(true);

      // Validaciones adicionales
      if (!newUnidad.name || newUnidad.name.trim() === '') {
        toast.error('El nombre de la unidad es requerido');
        return;
      }

      if (!newUnidad.ciudad || newUnidad.ciudad.trim() === '') {
        toast.error('La ciudad es requerida');
        return;
      }

      if (!newUnidad.email_unidad || newUnidad.email_unidad.trim() === '') {
        toast.error('El email de la unidad es requerido');
        return;
      }

      const unidadData = {
        name: newUnidad.name,
        ciudad: newUnidad.ciudad,
        fecha: new Date().toISOString(),
        email_unidad: newUnidad.email_unidad,
        telefono: newUnidad.telefono || '',
        businness_unidad: newUnidad.businness_unidad || 0,
        number_homes: newUnidad.number_homes || 0,
        company_patner_id: selectedCompanyPatnerId,
        // Datos bancarios
        banco: newUnidad.banco || '',
        numero_Cuenta: newUnidad.numero_Cuenta || '',
        tipo_Cuenta: newUnidad.tipo_Cuenta || '',
        nombre_Titular: newUnidad.nombre_Titular || '',
        // Datos breve
        type_LLave: newUnidad.type_LLave || '',
        number_LLave: newUnidad.number_LLave || '',
        titular_Breve: newUnidad.titular_Breve || ''
      };

      await unidadesResidencialesService.createUnidadResidencial(unidadData);
      await loadCompanyPatners(); // Recargar para actualizar el contador de unidades
      
      // Recargar las unidades del company patner actual
      if (selectedCompanyPatnerId) {
        await loadUnidadesByCompanyPatner(selectedCompanyPatnerId);
      }

      toast.success('Unidad residencial agregada exitosamente');
      handleCloseUnitFormModal();
    } catch (error) {
      toast.error('Error al agregar unidad residencial: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUnidad = async (unidadId) => {
    const unidad = unidadesResidenciales.find(u => u.id === unidadId);
    if (unidad) {
      const newName = prompt('Nuevo nombre de la unidad:', unidad.name);
      if (newName && newName.trim() !== '') {
        try {
          setLoading(true);
          await unidadesResidencialesService.updateUnidadResidencial(unidadId, {
            ...unidad,
            name: newName.trim()
          });
          
          // Recargar las unidades del company patner actual
          if (selectedCompanyPatnerId) {
            await loadUnidadesByCompanyPatner(selectedCompanyPatnerId);
          }
          
          toast.success('Unidad residencial actualizada exitosamente');
        } catch (error) {
          toast.error('Error al actualizar unidad residencial: ' + (error.message || 'Error desconocido'));
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleDeleteUnidad = async (unidadId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta unidad residencial?')) {
      try {
        setLoading(true);
        await unidadesResidencialesService.deleteUnidadResidencial(unidadId);
        await loadCompanyPatners(); // Recargar para actualizar el contador de unidades
        
        // Recargar las unidades del company patner actual
        if (selectedCompanyPatnerId) {
          await loadUnidadesByCompanyPatner(selectedCompanyPatnerId);
        }

        toast.success('Unidad residencial eliminada exitosamente');
      } catch (error) {
        toast.error('Error al eliminar unidad residencial: ' + (error.message || 'Error desconocido'));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      {loading && (
        <div className="text-center py-4">
          <p className="text-gray-600">Cargando...</p>
        </div>
      )}

      {/* Contenedor de company patners */}
      <div style={{ height: 'calc(100vh - 250px)' }}>
        <CompanyProvidersContainer
          companies={companyPatners}
          onSelectCompany={handleSelectCompanyPatner}
          onAddCompany={handleAddCompanyPatner}
          onUpdateCompany={handleUpdateCompanyPatner}
          onDeleteCompany={handleDeleteCompanyPatner}
          onOpenFormModal={handleOpenFormModal}
        />
      </div>

      {/* Modal de formulario de company patner */}
      <CompanyPatnerFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseFormModal}
        onSubmit={handleAddCompanyPatner}
      />

      {/* Modal de unidades residenciales */}
      <ResidentialUnitsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        companyName={selectedCompanyPatnerName}
      >
        {loadingUnidades ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando unidades residenciales...</p>
          </div>
        ) : (
          <ResidentialUnitsContainer
            units={filteredUnits}
            selectedCompanyId={selectedCompanyPatnerId}
            onAddUnit={handleAddUnidad}
            onUpdateUnit={handleUpdateUnidad}
            onDeleteUnit={handleDeleteUnidad}
            onOpenFormModal={handleOpenUnitFormModal}
          />
        )}
        
        {/* Modal de formulario de unidad residencial */}
        <ResidentialUnitFormModal
          isOpen={isUnitFormModalOpen}
          onClose={handleCloseUnitFormModal}
          onSubmit={handleAddUnidad}
          selectedCompanyId={selectedCompanyPatnerId}
        />
      </ResidentialUnitsModal>
    </>
  );
};

export default CompanyProvidersTab;
